/**
 * SidecarLoader — fetches per-asset JSON sidecars.
 *
 * Sidecars carry origin-correction (translateMm + rotationDegEuler) plus
 * path metadata. They live next to the GLB at <baseUrl>/<id>.meta.json (or
 * <id>.tool.meta.json for mover tools).
 *
 * On 404 the loader falls back to default origin-correction `(0,0,0)` /
 * `(0,0,0)`. The renderer can still place the GLB; the calibration tool is
 * the authoritative source for sidecar values.
 */

import type {
  ModuleType3D,
  MoverToolType3D,
  MoverType3D,
  RailSystem,
  RailType3D,
  Vec3,
} from '../types.js';

export interface OriginCorrection {
  translateMm: Vec3;
  rotationDegEuler: Vec3;
}

export interface ModuleSidecar {
  moduleType: ModuleType3D;
  glbByRailSystem: {
    Beckhoff: string | null;
    HepcoGfx?: string | null;
  };
  originCorrection: OriginCorrection;
  pathType: 'Straight' | 'Curve' | 'Free';
  moduleLengthMm: number;
  endAngleDeg: number;
  freePathFile?: string;
  approximateBoundsMm?: { min: Vec3; max: Vec3 };
}

export interface MoverSidecar {
  moverType: MoverType3D;
  glb: string;
  originCorrection: OriginCorrection;
  magnetPlateCenterMm: Vec3;
  pathLengthMm: number;
}

export interface MoverToolSidecar {
  toolType: MoverToolType3D;
  glbUrl: string;
  originCorrection: OriginCorrection;
  defaultOffsetMm: Vec3;
  approximateBoundsMm?: { min: Vec3; max: Vec3 };
  label: string;
}

/**
 * Calibration sidecar for a guiding-rail GLB. The rail is rendered in the
 * same module-local frame as the module (i.e. under the module's
 * `startWorldPose`) with this correction applied to the GLB scene.
 */
export interface RailSidecar {
  railType: RailType3D;
  glb: string;
  originCorrection: OriginCorrection;
  approximateBoundsMm?: { min: Vec3; max: Vec3 };
}

export const DEFAULT_ORIGIN_CORRECTION: OriginCorrection = Object.freeze({
  translateMm: [0, 0, 0] as Vec3,
  rotationDegEuler: [0, 0, 0] as Vec3,
});

/**
 * Generic sidecar fetch with promise caching + graceful fallback. Returns
 * `undefined` on 404 (caller substitutes a default), and surfaces network or
 * parse errors as `Error` (caller is expected to forward to onError).
 */
export class SidecarLoader {
  private readonly cache = new Map<string, Promise<unknown>>();

  fetchModule(url: string): Promise<ModuleSidecar | undefined> {
    return this.fetchJson<ModuleSidecar>(url);
  }

  fetchMover(url: string): Promise<MoverSidecar | undefined> {
    return this.fetchJson<MoverSidecar>(url);
  }

  fetchTool(url: string): Promise<MoverToolSidecar | undefined> {
    return this.fetchJson<MoverToolSidecar>(url);
  }

  fetchRail(url: string): Promise<RailSidecar | undefined> {
    return this.fetchJson<RailSidecar>(url);
  }

  clear(): void {
    this.cache.clear();
  }

  private fetchJson<T>(url: string): Promise<T | undefined> {
    if (!url) return Promise.resolve(undefined);
    const cached = this.cache.get(url) as Promise<T | undefined> | undefined;
    if (cached) return cached;
    const promise: Promise<T | undefined> = (async () => {
      try {
        const res = await fetch(url);
        if (res.status === 404) return undefined;
        if (!res.ok) {
          throw new Error(
            `Sidecar "${url}" failed with status ${res.status}`,
          );
        }
        return (await res.json()) as T;
      } catch (err) {
        // Network error: drop from cache so a retry can succeed.
        this.cache.delete(url);
        throw err;
      }
    })();
    this.cache.set(url, promise);
    return promise;
  }
}

/**
 * Resolve the effective origin correction for a module sidecar — falls back
 * to identity when the sidecar is absent.
 */
export function resolveOriginCorrection(
  sidecar: { originCorrection?: OriginCorrection } | undefined,
): OriginCorrection {
  return sidecar?.originCorrection ?? DEFAULT_ORIGIN_CORRECTION;
}

/** Standard sidecar filename from a base id. */
export function moduleSidecarFilename(moduleType: ModuleType3D): string {
  // The merged 180° clothoid lives in a single GLB whose calibration is
  // edited via the base half's sidecar (AT2050-0500.meta.json /
  // ATH2050-0500.meta.json). normalizeXtsConfig replaces the halves with
  // the merged id at render time, so route the sidecar lookup back here.
  if (moduleType === 'AT2050_0500_180') return 'AT2050-0500.meta.json';
  if (moduleType === 'ATH2050_0500_180') return 'ATH2050-0500.meta.json';
  return `${moduleType.replace(/_/g, '-')}.meta.json`;
}

export function moverSidecarFilename(moverType: MoverType3D): string {
  return `${moverType.replace(/_/g, '-')}.meta.json`;
}

export function toolSidecarFilename(toolType: MoverToolType3D): string {
  return `${toolType.replace(/_/g, '-')}.tool.meta.json`;
}

export function railSidecarFilename(railType: RailType3D): string {
  return `${railType.replace(/_/g, '-')}.rail.meta.json`;
}

/**
 * Pure helper: pick the GLB filename for a moduleSidecar with rail-system
 * fallback. Returns null when the sidecar marks the module 'prepared but
 * not delivered'.
 */
export function pickGlbForRail(
  sidecar: ModuleSidecar | undefined,
  rail: RailSystem,
): string | null {
  if (!sidecar) return null;
  if (rail === 'HepcoGfx' && sidecar.glbByRailSystem.HepcoGfx) {
    return sidecar.glbByRailSystem.HepcoGfx;
  }
  return sidecar.glbByRailSystem.Beckhoff ?? null;
}
